import { DateTime } from 'luxon'
import { Button, Link, Text } from '@/componentsV1/BaseComponents'
import { selectIsCurrentUserScorp } from '@/selectors/user.selectors'
import { convertUtcToLocalDate } from '@/utils/dateHelpers'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import {
  selectCurrentAnnualTaxYear,
  selectIsAfter1099NECDueDate,
  selectTaxDetailsByYear,
} from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import {
  getAnnualTaxFilingForYearSelector,
  selectAnnualTaxFilingForCurrentTaxYear,
  selectCurrentAnnualTaxFiling,
  selectHasBeenConnectedToTaxfyle,
} from '@/features/taxes/annual-taxes/annualTaxFilings.selector'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useCallback, useEffect, useState } from 'react'
import Ten99OptOutModal from '../../ten-99-filing/Ten99OptOutModal'
import { useNavigate } from 'react-router-dom'
import {
  FETCH_TAX_CONSULTATIONS_KEY,
  fetchTaxConsultationRequests,
  inProgressConsultationStatuses,
  TaxConsultationRequest,
  TaxConsultationServiceRequestStatus,
} from '@/features/taxes/advisory/taxConsultationRequest.actions'

import TaxOptOutModal from '../../tax-opt-in/TaxOptOutModal'
import { selectIsFetchingForKeys } from '@/reducers/fetch'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '@/flags'
import {
  get1099NecMagicLink,
  getCanOptOutOf1099Nec,
  needContractorFilingOption,
  REQUEST_1099_MAGIC_LINK_KEY,
} from '@/features/taxes/annual-taxes/annualTaxFilings.slice'
import { useOpenTaxfylePortal } from '@/features/taxes/annual-taxes/useOpenTaxfylePortal'
import { TaxfylePortalError } from '@/features/taxes/annual-taxes/components/TaxfylePortalError'
import { select1099NecYearEndModuleStatus } from '@/features/year-end-module-status/yearEndModuleStatus.selectors'
import { YearEndModuleStatusOptions } from '@/features/year-end-module-status/yearEndModuleStatus.slice'
import { get1099NecThresholdDisplay } from '@/features/taxes/annual-taxes/constants'

export const FAQsSection = () => {
  const filing = useReselector(selectAnnualTaxFilingForCurrentTaxYear)

  if (filing?.optedOutAt) {
    return null
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Text as="h2">FAQs</Text>
      <div>
        <Link
          href="https://support.joinheard.com/hc/en-us/articles/19788518004887-Taxfyle-FAQs"
          newPage
        >
          Why can’t I connect with a tax preparer yet?
        </Link>
      </div>
      <div>
        <Link
          as="secondaryLink"
          color="forest"
          href="https://support.joinheard.com/hc/en-us/articles/20989977806231-Annual-Tax-FAQs"
          newPage
        >
          View All FAQs
        </Link>
      </div>
    </div>
  )
}

export const OptIntoExtension = ({ taxYear }: { taxYear: string }) => {
  const navigate = useNavigate()

  const enableExtensionsOptIn = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableExtensionsOptIn,
    false
  )
  const isScorp = useReselector(selectIsCurrentUserScorp)
  const now = DateTime.now()
  const filing = useReselector(getAnnualTaxFilingForYearSelector, taxYear)
  const annualTaxDetails = useReselector(selectTaxDetailsByYear, taxYear)

  if (!enableExtensionsOptIn) {
    return null
  }
  if (!annualTaxDetails) {
    return null
  }
  if (filing?.extensionRequestedAt || filing?.optedOutAt) {
    return null
  }
  const scorpExtensionRequestCutoffDate = convertUtcToLocalDate(
    annualTaxDetails.form_1120_s_extension_survey_cutoff_date
  )?.endOf('day')
  const sPropExtensionRequestCutoffDate = convertUtcToLocalDate(
    annualTaxDetails.form_1040_extension_survey_cutoff_date
  )?.endOf('day')
  if (
    isScorp &&
    scorpExtensionRequestCutoffDate &&
    now > scorpExtensionRequestCutoffDate
  ) {
    return null
  } else if (
    !isScorp &&
    sPropExtensionRequestCutoffDate &&
    now > sPropExtensionRequestCutoffDate
  ) {
    return null
  }
  return (
    <div>
      <Button
        variant="link"
        onClick={() => navigate('/taxes/annual/extension_request')}
      >
        Get more time to file →
      </Button>
      <Text as="bodySm" style={{ paddingTop: 4 }}>
        Need more time? Requesting an extension is quick and easy.
      </Text>
    </div>
  )
}

export const OptInto1099NEC = ({ taxYear }: { taxYear: string }) => {
  const [modalOpen, setModalOpen] = useState(false)

  const isAfter1099NECDueDate = useReselector(selectIsAfter1099NECDueDate)
  if (isAfter1099NECDueDate) {
    return null
  }

  return (
    <div>
      <Button variant="link" onClick={() => setModalOpen(true)}>
        File 1099-NEC through Taxfyle →
      </Button>
      <Text as="bodySm" style={{ paddingTop: 4 }}>
        If you&apos;ve paid one or more contractors{' '}
        {get1099NecThresholdDisplay(taxYear)} or more throughout {taxYear}{' '}
        you&apos;ll need to file a 1099-NEC. You can use your trusted partner
        Taxfyle, and we&apos;ll cover the costs.
      </Text>
      <Ten99OptOutModal
        year={taxYear}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        optIn
      />
    </div>
  )
}

export const OptOutOf1099NEC = () => {
  const status = useReselector(select1099NecYearEndModuleStatus)
  const currentTaxFiling = useReselector(selectCurrentAnnualTaxFiling)
  const canOptOutRes = useFetchResponse(getCanOptOutOf1099Nec)
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const [modalOpen, setModalOpen] = useState(false)

  const ableToOptOut = Boolean(canOptOutRes?.ableToOptOut)

  const {
    open: openPortal,
    loading: portalLoading,
    error: portalError,
  } = useOpenTaxfylePortal(get1099NecMagicLink, REQUEST_1099_MAGIC_LINK_KEY)

  const onClick = useCallback(() => {
    if (ableToOptOut) {
      setModalOpen(true)
    } else {
      void openPortal()
    }
  }, [ableToOptOut, openPortal])

  if (
    currentTaxFiling?.needContractorFiling === needContractorFilingOption.no ||
    status === YearEndModuleStatusOptions.complete
  ) {
    return null
  }

  return (
    <div>
      <Button variant="link" loading={portalLoading} onClick={onClick}>
        Opt out of 1099-NEC filing →
      </Button>
      <TaxfylePortalError error={portalError} />
      <Text as="bodySm" style={{ paddingTop: 4 }}>
        {ableToOptOut
          ? 'You can opt out of 1099-NEC filing since your filing hasn’t been started yet.'
          : 'Your 1099-NEC filing is already in progress. To cancel or make changes, you’ll need to work directly with your Taxfyle 1099 preparer.'}
      </Text>
      <Ten99OptOutModal
        year={currentTaxYear}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        optIn={false}
      />
    </div>
  )
}

export const OptIntoTaxesWithHeard = ({ taxYear }: { taxYear: string }) => {
  const optIntoTaxesSidebarEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.optIntoTaxesSidebarEnabled,
    false
  )

  const isScorp = useReselector(selectIsCurrentUserScorp)
  const annualTaxDetails = useReselector(selectTaxDetailsByYear, taxYear)
  const navigate = useNavigate()

  if (!annualTaxDetails) {
    return null
  }
  if (!optIntoTaxesSidebarEnabled) {
    return null
  }
  const scorpDueDate = convertUtcToLocalDate(
    annualTaxDetails.form_1120_s_tq_due_date
  )?.endOf('day')
  const sPropDueDate = convertUtcToLocalDate(
    annualTaxDetails.form_1040_tq_due_date
  )?.endOf('day')

  // Opt into taxes (valid through Mar 3 and Apr 1 for S-corps and Sole Props respectively)
  // after that, don't show this option
  const now = DateTime.now()
  if (isScorp && scorpDueDate && now > scorpDueDate) {
    return null
  } else if (!isScorp && sPropDueDate && now > sPropDueDate) {
    return null
  }

  return (
    <div>
      <Button variant="link" onClick={() => navigate('/taxes/annual/opt-in')}>
        Opt in to annual taxes →
      </Button>
      <Text as="bodySm" style={{ paddingTop: 4 }}>
        Opt back into filing your taxes with Heard.
      </Text>
    </div>
  )
}

export const OptOutOfTaxesWithHeard = ({
  setOpenModal,
}: {
  setOpenModal: (open: boolean) => void
}) => {
  const hasConnectedtoTaxPro = useReselector(selectHasBeenConnectedToTaxfyle)
  if (hasConnectedtoTaxPro) {
    return null
  }
  return (
    <div>
      <Button variant="link" onClick={() => setOpenModal(true)}>
        Opt out of annual taxes →
      </Button>
      <Text as="bodySm" style={{ paddingTop: 4 }}>
        If you no longer require tax services from Heard, start the opt out
        process here.
      </Text>
    </div>
  )
}

export const OptInOrOutWithHeard = ({
  taxYear,
  optedOutOfTaxesWithHeard,
}: {
  taxYear: string
  optedOutOfTaxesWithHeard: boolean
}) => {
  const [openModal, setOpenModal] = useState(false)
  return (
    <>
      <TaxOptOutModal open={openModal} close={() => setOpenModal(false)} />
      {optedOutOfTaxesWithHeard ? (
        <OptIntoTaxesWithHeard taxYear={taxYear} />
      ) : (
        <OptOutOfTaxesWithHeard setOpenModal={setOpenModal} />
      )}
    </>
  )
}

export const GetTaxAdvice = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const filing = useReselector(selectAnnualTaxFilingForCurrentTaxYear)
  const [inProgressConsultationRequest, setInProgressConsultationRequest] =
    useState<TaxConsultationRequest>()
  const [hasPreviousConsultationRequest, setHasPreviousConsultationRequest] =
    useState(false)
  const loading = useReselector(selectIsFetchingForKeys, [
    FETCH_TAX_CONSULTATIONS_KEY,
  ])
  const goToConsultPage = useCallback(() => {
    if (inProgressConsultationRequest) {
      navigate(
        `/taxes/annual/tax_consultations/${inProgressConsultationRequest.id}/confirmed`
      )
    } else {
      navigate('/taxes/annual/tax_consultations')
    }
  }, [inProgressConsultationRequest, navigate])

  useEffect(() => {
    const loadRequests = async () => {
      const res = (await fetchTaxConsultationRequests()(dispatch)) ?? []
      setInProgressConsultationRequest(
        res.find((r) => inProgressConsultationStatuses.includes(r.status))
      )
      setHasPreviousConsultationRequest(
        res.some(
          (r) => r.status === TaxConsultationServiceRequestStatus.completed
        )
      )
    }
    loadRequests()
  }, [dispatch])

  if (filing?.optedOutAt) {
    return null
  }

  return (
    <div>
      <Button loading={loading} variant="link" onClick={goToConsultPage}>
        Get{hasPreviousConsultationRequest ? ' more' : ''} tax advice →
      </Button>
      {!loading && (
        <Text as="bodySm" style={{ paddingTop: 4 }}>
          {inProgressConsultationRequest
            ? 'Reach out to your tax preparer via the chat portal to book a 1:1 consult with them.'
            : hasPreviousConsultationRequest
              ? 'You can book another live call with your tax preparer for a fee.'
              : 'After you complete your tax questionnaire, you can book a 1:1 consult with your tax preparer.'}
        </Text>
      )}
    </div>
  )
}
